var kind       = require("enyo/kind"),
    dom        = require("enyo/dom"),
    CoreObject = require("enyo/CoreObject"),
    Animation  = require("enyo-anim/Animation"),
    Animator   = require("enyo-anim/Animator"),
    Attractor  = require("enyo-anim/Attractor"),
    Scalar     = require("enyo-anim/Scalar");

/**
 * [Animated2DTransform]{@link enyo-anim.Animated2DTransform} is a specialized
 * [Animation]{@link enyo-anim.Animation} object used for animating css
 * transforms in 2D space.
 *
 * For the sake of performance, we don't implement any type checking. Please
 * supply the parameters within the documented types and ranges.
 *
 * @class   enyo-anim.Animated2DTransform
 * @extends enyo-anim.Animation
 * @public
 */
module.exports = kind(/** @lends enyo-anim.Animated2DTransform.prototype */ {

    /**
     * @private
     */
    name: "enyo-anim.Animated2DTransform",

    /**
     * @private
     */
    kind: Animation,

    /**
     * Unscaled width in pixels.
     *
     * @type    {number}
     * @default 0
     * @public
     */
    width: 0,

    /**
     * Unscaled height in pixels.
     *
     * @type    {number}
     * @default 0
     * @public
     */
    height: 0,

    /**
     * The amount of pixels below which we consider velocity or distance to be
     * too small to be significant (Used to break zeno's paradox).
     *
     * @type    {number}
     * @default 0.5
     * @public
     */
    epsilon: 0.5,

    /**
     * Feature flag sets whether css transforms can be accelerated.
     *
     * @type {boolean}
     * @readonly
     * @public
     */
    canAccelerate: null,

    /**
     * Instantiates animators for transform origin, position and scale.
     *
     * @private
     */
    importProps: function() {
        CoreObject.prototype.importProps.apply(this, arguments);
        this.canAccelerate = dom.canAccelerate();
        this.animators     = {
            originX: new Animator({
                scalar: new Scalar({
                    value:    null,
                    friction: 1
                }),
                attractors: {
                    target: new Attractor({
                        attraction: 0.035
                    })
                }
            }),
            originY: new Animator({
                scalar: new Scalar({
                    value:    null,
                    friction: 1
                }),
                attractors: {
                    target: new Attractor({
                        attraction: 0.035
                    })
                }
            }),
            positionX: new Animator({
                scalar: new Scalar({
                    value:    null,
                    friction: 0.71
                }),
                attractors: {
                    target: new Attractor({
                        attraction: 0.035
                    })
                }
            }),
            positionY: new Animator({
                scalar: new Scalar({
                    value:    null,
                    friction: 0.71
                }),
                attractors: {
                    target: new Attractor({
                        attraction: 0.035
                    })
                }
            }),
            scaleX: new Animator({
                scalar: new Scalar({
                    value:    null,
                    friction: 0.71
                }),
                attractors: {
                    target: new Attractor({
                        attraction: 0.035
                    })
                }
            }),
            scaleY: new Animator({
                scalar: new Scalar({
                    value:    null,
                    friction: 0.71
                }),
                attractors: {
                    target: new Attractor({
                        attraction: 0.035
                    })
                }
            })
        };
    },

    // Epsilon
    // --------------------------------

    /**
     * Update [epsilon]{@link enyo-anim.Scalar#epsilon} on X-axis in response to
     * change in [width]{@link enyo-anim.Animated2DTransform#width}.
     *
     * @private
     */
    widthChanged: function() {
        this.applyEpsilonX();
    },

    /**
     * Update [epsilon]{@link enyo-anim.Scalar#epsilon} on Y-axis in response to
     * change in [height]{@link enyo-anim.Animated2DTransform#height}.
     *
     * @private
    */
    heightChanged: function() {
        this.applyEpsilonY();
    },

    /**
     * Apply changed [epsilon]{@link enyo-anim.Animated2DTransform#epsilon} on
     * both X-axis and Y-axis.
     *
     * @private
     */
    epsilonChanged: function() {
        this.applyEpsilonX();
        this.applyEpsilonY();
    },

    /**
     * Apply [epsilon]{@link enyo-anim.Animated2DTransform#epsilon} on X-axis.
     *
     * @private
     */
    applyEpsilonX: function() {
        var epsilon   = this.epsilon,
            animators = this.animators,
            width     = this.width;

        // Don't break zeno's paradox
        if (!epsilon || epsilon <= 0) {
            animators.originX.scalar.epsilon   = 0;
            animators.positionX.scalar.epsilon = 0;
            animators.scaleX.scalar.epsilon    = 0;

            return;
        }

        animators.originX.scalar.epsilon   = epsilon;
        animators.positionX.scalar.epsilon = epsilon;

        // Guard against invalid width or division by 0
        if (width) {
            animators.scaleX.scalar.epsilon = 1 / (width / epsilon);
        }
    },

    /**
     * Apply [epsilon]{@link enyo-anim.Animated2DTransform#epsilon} on Y-axis.
     *
     * @private
     */
    applyEpsilonY: function() {
        var epsilon   = this.epsilon,
            animators = this.animators,
            height    = this.height;

        // Don't break zeno's paradox
        if (!epsilon || epsilon <= 0) {
            animators.originY.scalar.epsilon   = 0;
            animators.positionY.scalar.epsilon = 0;
            animators.scaleY.scalar.epsilon    = 0;

            return;
        }

        animators.originY.scalar.epsilon   = epsilon;
        animators.positionY.scalar.epsilon = epsilon;

        // Guard against invalid width or division by 0
        if (height) {
            animators.scaleY.scalar.epsilon = 1 / (height / epsilon);
        }
    },

    // CSS Transform
    // --------------------------------

    /**
     * Generates css transform properties from current animation state.
     *
     * @return {Object}  The transform properties
     * @public
     */
    generateCssTransform: function() {
        var animators = this.animators,
            transform = {
                originX:   animators.originX.scalar.value,
                originY:   animators.originY.scalar.value,
                positionX: animators.positionX.scalar.value,
                positionY: animators.positionY.scalar.value,
                positionZ: this.canAccelerate ? 0 : null,
                scaleX:    animators.scaleX.scalar.value,
                scaleY:    animators.scaleY.scalar.value
            };

        if (transform.originX !== null) {
            transform.originX += "px";
        }

        if (transform.originY !== null) {
            transform.originY += "px";
        }

        if (transform.positionX !== null) {
            transform.positionX += "px";
        }

        if (transform.positionY !== null) {
            transform.positionY += "px";
        }

        return transform;
    }

});
